import {testIdProps} from '@github-ui/test-id-props'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {useSyncedState} from '@github-ui/use-synced-state'
import {FocusKeys} from '@primer/behaviors'
import {
  ActionList,
  type ActionListItemProps,
  ActionMenu,
  AnchoredOverlay,
  type AnchoredOverlayProps,
  Button,
  type ButtonProps,
  FormControl,
  TextInput,
} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {subYears} from 'date-fns'
import {memo, type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {CustomColumnKind} from '../../api/columns/contracts/storage'
import type {IssueField} from '../../api/common-contracts'
import {SettingsFieldOptionHeaderUI, SettingsFieldOptionSave} from '../../api/stats/contracts'
import {
  type ColumnDropdownItem,
  createApiRequest,
  createFieldTypeItems,
  getDefaultColumnTitle,
} from '../../helpers/add-field-helpers'
import {replaceShortCodesWithEmojis} from '../../helpers/emojis'
import {defaultIterationDuration} from '../../helpers/iterations'
import {filterEmptyOptions} from '../../helpers/new-column'
import {not_typesafe_nonNullAssertion} from '../../helpers/non-null-assertion'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useMemexRootHeight} from '../../hooks/use-memex-root-height'
import {useViews} from '../../hooks/use-views'
import type {ColumnModel} from '../../models/column-model'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import {useReservedColumnNames} from '../../state-providers/columns/use-reserved-column-names'
import {useIssueFieldsEnabled} from '../../state-providers/issue-fields/use-issue-fields-enabled'
import {useIssueFieldsQuery} from '../../state-providers/issue-fields/use-issue-fields-query'
import {DialogType, type RenameState} from '../../state-providers/modals/add-field-modal-state-provider'
import {useAddFieldModal} from '../../state-providers/modals/use-add-field-modal'
import {Resources} from '../../strings'
import {validateColumnTitle} from '../../validations/validate-column-title'
import {AddFieldsDialog} from '../add-fields-dialog'
import {getColumnIcon} from '../column-detail-helpers'
import {EmojiAutocomplete} from '../common/emoji-autocomplete'
import {NewIterationOptions} from '../fields/iteration/new-iteration-options'
import {SingleSelectForm} from '../fields/single-select/single-select-form'
import {RenameProjectFieldDialog} from '../rename-project-field-dialog'
import styles from './add-column-modal.module.css'
import {useAddField} from './hooks/use-add-field'
import {useColumnSettings} from './use-column-settings'

function getDialog(isOpen: boolean, suggestedIssueFieldsLength: number, issueFieldsLoading: boolean) {
  if (!isOpen) return null
  if (issueFieldsLoading) return DialogType.IssueFields
  if (suggestedIssueFieldsLength > 0) return DialogType.IssueFields
  return DialogType.AddColumn
}

function limitedKindsOnly(fields: Array<IssueField>, limitedTypes?: Array<CustomColumnKind>) {
  if (!limitedTypes || limitedTypes.length === 0) return fields
  const set = new Set(limitedTypes)
  return fields.filter(field => set.has(field.dataType))
}

type ModalProps = {
  isOpen: boolean
  anchorRef: React.RefObject<HTMLElement | null>
  setOpen: (open: boolean) => void
  onSave: (newCols: Array<ColumnModel>) => void
  limitedTypes?: Array<CustomColumnKind>
}

// extact the possible gesture strings for comparison in the close handler
type CloseOverlayGesture = Parameters<NonNullable<AnchoredOverlayProps['onClose']>>[0]

export const AddColumnModal = memo(function AddColumnModal(props: ModalProps) {
  const issueFieldsEnabled = useIssueFieldsEnabled()

  if (issueFieldsEnabled) {
    return <AddColumnModalV2Wrapper {...props} />
  }

  return <AddColumnModalLegacy {...props} />
})

const EMPTY_ISSUE_FIELDS: Array<IssueField> = []

export const AddColumnModalV2Wrapper = memo(function AddColumnModalV2Wrapper({setOpen, isOpen, ...props}: ModalProps) {
  const {data, status: suggestionsLoadingState} = useIssueFieldsQuery()
  const suggestedIssueFields = data?.issueFields ?? EMPTY_ISSUE_FIELDS
  const hasOrgOnlyFieldsHidden = data?.hasOrgOnlyFieldsHidden ?? false
  const hasLoadedSuggestions = suggestionsLoadingState !== 'pending'
  const {dialog, setDialog, closeAddColumnModal, openRenameDialog, handleRenameConfirm, handleRenameCancel} =
    useAddFieldModal()
  const [persistentSelectedFieldIds, setPersistentSelectedFieldIds] = useState<Set<number>>(() => new Set())

  // In roadmap context, only date fields should be shown
  const filteredIssueFields = useMemo(
    () => limitedKindsOnly(suggestedIssueFields, props.limitedTypes),
    [props.limitedTypes, suggestedIssueFields],
  )
  const numIssueFields = filteredIssueFields.length

  // If we've previously loaded issue fields and there were none, don't default to showing the loading state.
  // Instead, automatically advance them to the "Create project fields" dialog.
  const hadNoSuggestions = hasLoadedSuggestions && numIssueFields === 0
  const issueFieldsLoading = suggestionsLoadingState === 'pending' && !hadNoSuggestions
  // Separate state to control the loading spinner, needed to avoid empty state flashing in before the dialog switches to "Add project field"
  // in the event that the issue field list is empty.
  const [showIssueFieldsLoading, setShowIssueFieldsLoading] = useState(issueFieldsLoading)
  // Keep the loading state in a ref to run effect hooks only when loading state changes
  const isLoadingRef = useRef(issueFieldsLoading)
  const updateIssueFieldsLoading = useCallback((loading: boolean) => {
    isLoadingRef.current = loading
    setShowIssueFieldsLoading(loading)
  }, [])

  const isOpenRef: RefObject<boolean | null> = useRef(null)
  const closeDialog = useCallback(
    (open: boolean) => {
      if (!open) {
        setPersistentSelectedFieldIds(new Set())
        closeAddColumnModal()
      }
      setOpen(open)
    },
    [setOpen, closeAddColumnModal],
  )

  useEffect(() => {
    if (isOpen === isOpenRef.current) {
      // If the open state has not changed, avoid re-evaluating the dialog state
      return
    }
    isOpenRef.current = isOpen

    // Don't change dialog state when rename dialog is open to avoid interrupting the rename flow
    if (isOpen && dialog?.type !== DialogType.RenameField) {
      const newDialogType = getDialog(isOpen, numIssueFields, isLoadingRef.current)
      setDialog(newDialogType ? {type: newDialogType} : null)
    }
  }, [isOpen, isLoadingRef, numIssueFields, setDialog, dialog])

  useEffect(() => {
    if (issueFieldsLoading === isLoadingRef.current) return

    // If the dialog is not open, avoid re-evaluating the dialog state
    if (!isOpenRef.current) {
      updateIssueFieldsLoading(issueFieldsLoading)
      return
    }

    // If we've finished loading issue fields and there are none, automatically advance them to the "Create project fields" dialog.
    if (!issueFieldsLoading && numIssueFields === 0 && dialog?.type === DialogType.IssueFields) {
      setDialog({type: DialogType.AddColumn})
    }
    updateIssueFieldsLoading(issueFieldsLoading)
  }, [numIssueFields, issueFieldsLoading, dialog, updateIssueFieldsLoading, setDialog])

  const handleSelectFieldWithCollision = useCallback(
    (renameState: RenameState) => {
      openRenameDialog(renameState)
    },
    [openRenameDialog],
  )

  const openAddColumnDialog = useCallback(() => {
    setDialog({type: DialogType.AddColumn})
  }, [setDialog])

  const onAddFieldDialogBack = useMemo(() => {
    if (dialog?.type !== DialogType.AddColumn) return undefined
    if (filteredIssueFields.length === 0) return undefined

    return () => setDialog({type: DialogType.IssueFields})
  }, [filteredIssueFields, setDialog, dialog])

  const showAddFieldDialog = dialog?.type === DialogType.AddColumn || dialog?.type === DialogType.IssueFields

  return (
    <>
      {isOpen && showAddFieldDialog && (
        <AddFieldsDialog
          dialogType={dialog.type}
          setOpen={closeDialog}
          onSave={props.onSave}
          limitedTypes={props.limitedTypes}
          onBack={onAddFieldDialogBack}
          issueFields={filteredIssueFields}
          openAddColumnDialog={openAddColumnDialog}
          issueFieldsLoading={showIssueFieldsLoading}
          onSelectFieldWithCollision={handleSelectFieldWithCollision}
          selectedFieldIds={persistentSelectedFieldIds}
          setSelectedFieldIds={setPersistentSelectedFieldIds}
          hasOrgOnlyFieldsHidden={hasOrgOnlyFieldsHidden}
        />
      )}
      {isOpen && dialog?.type === DialogType.RenameField && (
        <RenameProjectFieldDialog
          column={dialog.renameState.column}
          onConfirm={handleRenameConfirm}
          onCancel={handleRenameCancel}
          onBack={dialog.renameState.source === 'issue-fields-dialog' ? handleRenameCancel : undefined}
          variant="issue-field"
        />
      )}
    </>
  )
})

export const AddColumnModalLegacy = memo(function AddColumnModalInner({
  setOpen: setShowNewModal,
  isOpen,
  onSave,
  anchorRef,
  limitedTypes,
}: ModalProps) {
  const {allColumns} = useAllColumns()
  const numUserDefinedColumns = allColumns.filter(c => c.userDefined).length
  const defaultColumnTitle = getDefaultColumnTitle(numUserDefinedColumns)
  const {currentView} = useViews()
  const [title, setTitle] = useSyncedState(defaultColumnTitle, {isPropUpdateDisabled: isOpen})
  const initialFocusRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const {reservedColumnNames} = useReservedColumnNames()

  const {clientHeight} = useMemexRootHeight({})

  const maxOverlayHeight = useMemo(() => {
    if (!clientHeight) return 'unset'

    if (!anchorRef.current) return clientHeight

    const topDiff = window.pageYOffset + anchorRef.current.getBoundingClientRect().top
    return clientHeight - topDiff
  }, [anchorRef, clientHeight])

  const {options, addOption, updateOption, removeOption, onDrop, canRemoveOption, revertMutations} = useColumnSettings({
    ui: SettingsFieldOptionHeaderUI,
  })

  const {addField} = useAddField()

  const allFieldTypes: Array<ColumnDropdownItem> = useMemo(
    () => createFieldTypeItems({includeMouseDownHandler: true}),
    [],
  )

  const items =
    limitedTypes && limitedTypes.length ? allFieldTypes.filter(item => limitedTypes.includes(item.type)) : allFieldTypes
  const [selectedColumn, setSelectedColumn] = useState<CustomColumnKind>(
    () => not_typesafe_nonNullAssertion(items[0]).type,
  )
  const [dropdownSelection, setDropdownSelection] = useState(() => not_typesafe_nonNullAssertion(items[0])) // default is MemexColumnDataType.Text

  const resetTitle = useCallback(() => {
    setTitle(defaultColumnTitle)
  }, [defaultColumnTitle, setTitle])

  // Ideally we would be able to make the signature here: (item?: ColumnDropdownItem) => void
  // but it doesn't seem like this is supported by the onChange prop of the DropdownMenu currently
  const onDropdownChange: (item?: ActionListItemProps) => void = useCallback(
    item => {
      // item can be undefined if the item that has been activated is the same as the
      // one that is currently selected
      if (item) {
        const dropDownItem = item as ColumnDropdownItem
        if (title === defaultColumnTitle) {
          if (dropDownItem.type === MemexColumnDataType.Iteration) {
            const iterationColumns = allColumns.filter(c => c.userDefined && c.name.startsWith('Iteration')).length
            setTitle(Resources.newIterationField(iterationColumns > 0 ? ` ${iterationColumns + 1}` : ''))
          } else {
            resetTitle()
          }
        }
        setDropdownSelection(dropDownItem)
        setSelectedColumn(dropDownItem.type)
      }
    },
    [allColumns, defaultColumnTitle, resetTitle, setTitle, title],
  )

  // Use a static reference for this to avoid re-rendering and closing the datepicker
  const minIterationStartDate = useMemo(() => subYears(new Date(), 1), [])
  const [iterationStartDate, setIterationStartDate] = useState(() => new Date()) // default is today
  const [iterationDuration, setIterationDuration] = useState(defaultIterationDuration)
  const [isIterationOptionsValid, setIsIterationOptionsValid] = useState(true)

  const handleIterationValidationChange = useCallback((isValid: boolean) => {
    setIsIterationOptionsValid(isValid)
  }, [])

  const iterationValidation = useMemo(
    () => ({
      onChange: handleIterationValidationChange,
    }),
    [handleIterationValidationChange],
  )

  const resetFormState = useCallback(() => {
    resetTitle()
    revertMutations()

    setIterationStartDate(new Date())
    setIterationDuration(defaultIterationDuration)
    setIsIterationOptionsValid(true)

    const item = items[0]
    if (item) {
      setDropdownSelection(item)
      setSelectedColumn(item.type)
    }
  }, [items, resetTitle, revertMutations])

  useEffect(() => {
    if (isOpen) {
      // ensure the modal is visible
      containerRef.current?.scrollIntoView()
    }
  }, [isOpen])

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(replaceShortCodesWithEmojis(e.currentTarget.value))
    },
    [setTitle],
  )

  const defaultButtonProps: Omit<ButtonProps, 'children'> = {
    size: 'small',
    type: 'button',
  }

  const hasAtLeastOneOptionIfSingleSelect = useCallback(() => {
    if (selectedColumn !== MemexColumnDataType.SingleSelect) return true

    return filterEmptyOptions(options).length > 0
  }, [options, selectedColumn])

  const {isValid: isTitleValid, message: validationMessage} = validateColumnTitle(
    {allColumns, reservedColumnNames},
    title,
  )
  const isColumnValid = hasAtLeastOneOptionIfSingleSelect() && isTitleValid

  const closeModal = useCallback(
    (gesture?: CloseOverlayGesture) => {
      if (gesture !== 'click-outside') {
        resetFormState()
      }

      setShowNewModal(false)
    },
    [setShowNewModal, resetFormState],
  )

  const {postStats} = usePostStats()

  const saveNewColumn = useCallback(async () => {
    if (!isColumnValid) {
      return
    }

    closeModal()

    const request = createApiRequest(title, selectedColumn, {
      options,
      iterationStartDate,
      iterationDuration,
    })

    await addField.perform({request, viewNumber: currentView?.number})

    if (addField.status.current.status === 'succeeded') {
      onSave([addField.status.current.data])
    }

    postStats({
      name: SettingsFieldOptionSave,
      ui: SettingsFieldOptionHeaderUI,
      memexProjectColumnId: addField.status.current.data?.id,
    })
  }, [
    isColumnValid,
    closeModal,
    title,
    selectedColumn,
    options,
    iterationStartDate,
    iterationDuration,
    addField,
    currentView?.number,
    postStats,
    onSave,
  ])

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        saveNewColumn()
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'Escape') {
        closeModal()
        e.preventDefault()
      }
    },
    [closeModal, saveNewColumn],
  )
  const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(handleKeydown)

  const SelectedIcon = getColumnIcon(selectedColumn)

  const optionsLength = options.length
  const lastOptionsLength = useRef(options.length)

  useEffect(() => {
    lastOptionsLength.current = optionsLength
  })

  const saveText = selectedColumn === 'iteration' ? 'Save and create' : 'Save'

  if (!isOpen) return null
  return (
    <AnchoredOverlay
      anchorRef={anchorRef}
      renderAnchor={null}
      open={isOpen}
      onClose={closeModal}
      width="medium"
      overlayProps={{
        ...testIdProps('add-column-menu'),
        initialFocusRef,
        role: 'dialog',
        'aria-label': 'Create a new field configuration',
        style: maxOverlayHeight ? {maxHeight: maxOverlayHeight} : undefined,
        className: clsx(maxOverlayHeight ? styles.OverlayWithMaxHeight : styles.Overlay),
      }}
      focusZoneSettings={{bindKeys: FocusKeys.Tab, containerRef: anchorRef}}
    >
      <form onSubmit={e => e.preventDefault()}>
        <FormControl required className={styles.FormControl}>
          <FormControl.Label>Field name</FormControl.Label>
          <EmojiAutocomplete fullWidth>
            <TextInput
              {...inputCompositionProps}
              // select all text on focus
              onFocus={e => e.currentTarget.select()}
              value={title}
              className={isTitleValid ? '' : 'error'}
              onChange={handleTitleChange}
              ref={initialFocusRef}
              aria-invalid={!isTitleValid}
              {...testIdProps('add-column-name-input')}
              validationStatus={!isTitleValid ? 'error' : undefined}
            />
          </EmojiAutocomplete>
          {!isTitleValid && <FormControl.Validation variant="error">{validationMessage}</FormControl.Validation>}
        </FormControl>
        <FormControl id="add-column-type" className={styles.FormControl_1}>
          <FormControl.Label id="add-column-type-label">Field type</FormControl.Label>
          <ActionMenu>
            <ActionMenu.Button
              aria-labelledby="add-column-type-label add-column-type-content"
              id="add-column-type"
              className={styles.ActionMenu_Button}
              {...testIdProps('add-column-type')}
            >
              <div className={styles.Box}>
                <div>
                  <Octicon icon={SelectedIcon} className={styles.Octicon} />
                </div>
                <div id="add-column-type-content">{dropdownSelection.text}</div>
              </div>
            </ActionMenu.Button>
            <ActionMenu.Overlay>
              <ActionList selectionVariant="single">
                {items.map(item => {
                  const {key, ...rest} = item
                  return (
                    <ActionList.Item
                      key={key}
                      selected={dropdownSelection === item}
                      onSelect={() => onDropdownChange(item)}
                      {...rest}
                    >
                      <ActionList.LeadingVisual>
                        <item.leadingVisual />
                      </ActionList.LeadingVisual>
                      {item.text}
                    </ActionList.Item>
                  )
                })}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </FormControl>
        {selectedColumn === MemexColumnDataType.SingleSelect && (
          <fieldset className={styles.Box_1}>
            <legend className={styles.Box_2}>Options</legend>
            <SingleSelectForm
              options={options}
              canRemoveOption={canRemoveOption}
              onAddOption={addOption}
              onUpdateOption={updateOption}
              onRemoveOption={removeOption}
              onDrop={onDrop}
              noDuplicatedOptions={false}
              enableNameLengthValidation={false}
              enableDescriptionLengthValidation={false}
            />
          </fieldset>
        )}
        {selectedColumn === MemexColumnDataType.Iteration && (
          <fieldset className={styles.Box_1}>
            <legend className={styles.Box_2}>Options</legend>
            <div className={styles.Box_3}>
              <NewIterationOptions
                duration={iterationDuration}
                onDurationChange={setIterationDuration}
                validation={iterationValidation}
                startDate={iterationStartDate}
                onStartDateChange={setIterationStartDate}
                minStartDate={minIterationStartDate}
              />
            </div>
          </fieldset>
        )}
        <div className={styles.Box_4}>
          <Button {...defaultButtonProps} onClick={() => closeModal()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            {...testIdProps('add-column-modal-save')}
            {...defaultButtonProps}
            onClick={saveNewColumn}
            disabled={!isColumnValid || !isIterationOptionsValid}
            className={styles.Button}
          >
            {saveText}
          </Button>
        </div>
      </form>
    </AnchoredOverlay>
  )
})
